import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from 'app/store'
import {
  ActivityLogData,
  ActivityLogsRequest,
  getActivityLogsAPI
} from 'utils/api'
import { ErrorData, ErrorState } from 'features/error/errorSlice'
import { toTimestampTz } from 'utils/date'

export interface ActivityState {
  filters: {
    entityIds: Array<string>
    fromTime: string | null
    toTime: string | null
    query: string
    types: Array<string>
  }
  itemsPerPage: number
  currentPage: number
  total: number
  isLoading: boolean
  errors: ErrorData | null
  data: ActivityLogData[]
}

export const initialState: ActivityState = {
  filters: {
    entityIds: [],
    fromTime: null,
    toTime: null,
    query: '',
    types: []
  },
  total: 0,
  itemsPerPage: 10,
  currentPage: 1,
  isLoading: false,
  errors: null,
  data: []
}

export const getActivityLogs = createAsyncThunk(
  'activity/getActivityLogs',
  async (payload: ActivityLogsRequest, { getState, rejectWithValue }) => {
    try {
      const { filters } = (getState() as RootState).activity
      if (filters.types.length > 0) {
        payload.activity_types = filters.types.join()
      }
      if (filters.fromTime) {
        payload.time_start = toTimestampTz(
          new Date(filters.fromTime),
          'START',
          payload.companyCC!
        ).toString()
      }
      if (filters.toTime) {
        payload.time_end = toTimestampTz(
          new Date(filters.toTime),
          'END',
          payload.companyCC!
        ).toString()
      }
      if (filters.entityIds.length > 0) {
        payload.entity_ids = filters.entityIds.join()
      }
      payload.user_name = filters.query
      const { companyCC, ...filteredPayload } = payload
      return await getActivityLogsAPI(filteredPayload)
    } catch (error) {
      const { status, data } = error.response
      if (data || status) return rejectWithValue({ status, data })
      return error
    }
  },
  {
    condition: (_, { getState }) => {
      const { error } = getState() as {
        error: ErrorState
      }
      if (
        error.status === 429 &&
        Math.floor(Date.now() / 1000) < error.updateAt!
      ) {
        return false
      }
    },
    dispatchConditionRejection: true
  }
)

export const activitySlice = createSlice({
  name: 'activity',
  initialState: initialState,
  reducers: {
    setFilterEntity: (state, action) => {
      const { payload: value } = action
      const {
        filters: { entityIds }
      } = state
      if (entityIds.includes(value)) {
        let filteredEntityIds = entityIds.filter((id) => id !== value)
        state.filters.entityIds = filteredEntityIds
      } else {
        state.filters.entityIds = [...entityIds, value]
      }
    },
    setFilterQuery: (state, action) => {
      state.filters = {
        ...initialState.filters,
        query: action.payload
      }
      state.currentPage = initialState.currentPage
    },
    setFilter: (state, action) => {
      const { payload } = action
      if (!payload) return
      state.filters = {
        ...state.filters,
        ...payload
      }
      state.currentPage = initialState.currentPage
    },
    setFilterTypes: (state, action) => {
      const { payload: value } = action
      const {
        filters: { types }
      } = state
      if (types.includes(value)) {
        let filterTypes = types.filter((id) => id !== value)
        state.filters.types = filterTypes
      } else {
        state.filters.types = [...types, value]
      }
    },
    setFromTime: (state, action) => {
      state.filters.fromTime = action.payload
    },
    setToTime: (state, action) => {
      state.filters.toTime = action.payload
    },
    resetFilter: (state, action) => {
      if (!action.payload) {
        state.filters = initialState.filters
        return
      }
      const { types, entityIds, fromTime, toTime, query } = action.payload
      if (types) {
        state.filters.types = initialState.filters.types
      }
      if (entityIds) {
        state.filters.entityIds = initialState.filters.entityIds
      }
      if (fromTime) {
        state.filters.fromTime = initialState.filters.fromTime
      }
      if (toTime) {
        state.filters.toTime = initialState.filters.toTime
      }
      if (query) {
        state.filters.query = initialState.filters.query
      }
      state.currentPage = initialState.currentPage
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActivityLogs.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getActivityLogs.fulfilled, (state, action) => {
        const { payload } = action
        const { data } = payload
        state.isLoading = false
        state.data = data.activity_logs
        state.total = data.total
      })
      .addCase(
        getActivityLogs.rejected,
        (state, action: PayloadAction<any, string, any, any>) => {
          const { payload, error } = action
          state.isLoading = false
          state.errors = payload || {
            status: 500,
            data: `${error.name}: ${error.message}`
          }
        }
      )
  }
})

export const {
  setFilterEntity,
  setFilterQuery,
  setFilter,
  setFilterTypes,
  setFromTime,
  setToTime,
  setCurrentPage,
  setItemsPerPage,
  resetFilter,
  resetState
} = activitySlice.actions

export const activitySelector = (state: RootState) => state.activity

export default activitySlice.reducer
